import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight, ArrowDownRight, ArrowRight, TriangleAlert, CheckCircle2,
  CircleDashed, Users, FileText,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/MainLayout';
import Skeleton from '../components/ui/Skeleton';
import styles from '../styles/CommandCentre.module.css';

/**
 * WTSN command centre — the first screen.
 *
 * It replaces a grid of ten equal room cards, which asked you to choose a
 * destination before it told you anything. This answers four questions in the
 * order you actually ask them:
 *
 *   how is the business doing   headline figures against the previous period
 *   what is the trend           one revenue chart, not five
 *   who is performing           creators ranked, with their share
 *   what needs me               only things that are genuinely blocked or late
 *
 * Nothing here is a card unless the grouping earns it. The figures are a plain
 * row separated by rules; the creator table is a table; attention is a list.
 * Every number comes from the same endpoints the old page used — no data has
 * been invented to fill a layout.
 */

const RANGES = [
  { id: '7', label: '7D', days: 7 },
  { id: '30', label: '30D', days: 30 },
  { id: '90', label: '90D', days: 90 },
];

const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
const money2 = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* Local calendar days throughout. toISOString() converts to UTC first, so for
   part of every day it returns yesterday for anyone west of Greenwich — a bug
   this codebase has already shipped once. */
const localDay = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return localDay(d);
}
const today = () => localDay();

/** The first day of the month `back` months ago (0 = this month). */
const monthStart = (back) => {
  const n = new Date();
  return localDay(new Date(n.getFullYear(), n.getMonth() - back, 1));
};
/** The last day of that month — day 0 of the next one. */
const monthEnd = (back) => {
  const n = new Date();
  return localDay(new Date(n.getFullYear(), n.getMonth() - back + 1, 0));
};

/** Percentage change, or null when there is no baseline to compare against. */
function delta(current, previous) {
  if (!previous) return null;
  return ((current - previous) / previous) * 100;
}

function CommandCentre() {
  const { token, user } = useAuth();
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [range, setRange] = useState('30');
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [creators, setCreators] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [vault, setVault] = useState(null);
  const [thisMonth, setThisMonth] = useState(null);
  const [lastMonth, setLastMonth] = useState(null);

  const days = RANGES.find((r) => r.id === range)?.days ?? 30;

  const load = useCallback(async () => {
    setLoading(true);
    const get = async (url) => {
      const r = await fetch(url, { headers: authHeaders() });
      return r.ok ? r.json() : null;
    };
    try {
      // The previous window of the same length is what makes every headline
      // figure a comparison rather than a bare number.
      // The chart compares whole calendar months, which is the comparison
      // people actually make out loud ("are we up on last month"), so it needs
      // its own two windows — the range buttons above drive the headline
      // figures, not this.
      const [cur, prev, cr, tk, vt, mNow, mPrev] = await Promise.all([
        get(`/api/revenue?from=${isoDaysAgo(days)}&to=${today()}`),
        get(`/api/revenue?from=${isoDaysAgo(days * 2)}&to=${isoDaysAgo(days + 1)}`),
        get('/api/creators'),
        get('/api/tasks?limit=50'),
        get('/api/vault/stats'),
        get(`/api/revenue?from=${monthStart(0)}&to=${today()}`),
        get(`/api/revenue?from=${monthStart(1)}&to=${monthEnd(1)}`),
      ]);
      setCurrent(cur);
      setPrevious(prev);
      setThisMonth(mNow);
      setLastMonth(mPrev);
      setCreators((cr?.creators || []).filter((c) => !c.is_archived));
      setTasks(tk?.tasks || []);
      setVault(vt);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, days]);

  useEffect(() => { load(); }, [load]);

  const totals = current?.totals || {};
  const prevTotals = previous?.totals || {};

  /**
   * Net revenue this month against last, day of month by day of month.
   *
   * Keyed on the day number rather than the date so the two months lie on top
   * of each other and "are we ahead of where we were?" is answerable by looking
   * at which line is higher. Entries arrive per creator per day, so they are
   * summed first — otherwise this draws one jagged line per creator.
   *
   * Last month is drawn for its whole length; this month stops at today rather
   * than running along the bottom to the 31st, which would read as a collapse.
   */
  const series = useMemo(() => {
    const sumByDayOfMonth = (payload) => {
      const byDay = new Map();
      for (const e of payload?.entries || []) {
        const d = Number(String(e.date).slice(8, 10));
        byDay.set(d, (byDay.get(d) || 0) + (Number(e.net_revenue) || 0));
      }
      return byDay;
    };
    const now = sumByDayOfMonth(thisMonth);
    const before = sumByDayOfMonth(lastMonth);
    if (now.size === 0 && before.size === 0) return [];

    const today = new Date().getDate();
    const lastLength = new Date(new Date().getFullYear(), new Date().getMonth(), 0).getDate();
    const span = Math.max(today, lastLength, ...before.keys(), ...now.keys());

    const out = [];
    for (let d = 1; d <= span; d++) {
      out.push({
        day: d,
        thisMonth: d <= today ? (now.get(d) || 0) : null,
        lastMonth: d <= lastLength ? (before.get(d) || 0) : null,
      });
    }
    return out;
  }, [thisMonth, lastMonth]);

  const monthNames = useMemo(() => {
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return {
      now: now.toLocaleDateString(undefined, { month: 'long' }),
      prev: prev.toLocaleDateString(undefined, { month: 'long' }),
    };
  }, []);

  // Creators ranked by what they earned in the window, with their share of it.
  const leaderboard = useMemo(() => {
    const byCreator = new Map();
    for (const e of current?.entries || []) {
      const key = e.creator_id;
      const row = byCreator.get(key) || { id: key, name: e.stage_name || 'Unknown', gross: 0, net: 0, days: new Set() };
      row.gross += Number(e.gross_revenue) || 0;
      row.net += Number(e.net_revenue) || 0;
      row.days.add(String(e.date).slice(0, 10));
      byCreator.set(key, row);
    }
    const rows = [...byCreator.values()].sort((a, b) => b.gross - a.gross);
    const top = rows[0]?.gross || 1;
    return rows.map((r) => ({ ...r, activeDays: r.days.size, share: (r.gross / top) * 100 }));
  }, [current]);

  // Only what is genuinely blocked or late. A list that includes everything is
  // a list nobody reads.
  const attention = useMemo(() => {
    const items = [];
    const now = new Date();

    for (const t of tasks) {
      if (t.status === 'complete' || t.status === 'archived') continue;
      if (!t.due_date) continue;
      const due = new Date(`${String(t.due_date).slice(0, 10)}T00:00:00`);
      if (due < now) {
        const late = Math.floor((now - due) / 86_400_000);
        items.push({
          key: `task-${t.id}`,
          tone: 'danger',
          label: t.title,
          detail: `Task overdue by ${late} day${late === 1 ? '' : 's'}`,
          to: `/tasks/${t.id}`,
        });
      }
    }

    if (vault?.pendingApproval > 0) {
      items.push({
        key: 'vault-review',
        tone: 'warning',
        label: `${vault.pendingApproval} file${vault.pendingApproval === 1 ? '' : 's'} awaiting review`,
        detail: 'Uploaded content has not been approved yet',
        to: '/content?status=needs_review',
      });
    }

    const unlinked = creators.filter((c) => !c.infloww_account_id).length;
    if (unlinked > 0) {
      items.push({
        key: 'unlinked',
        tone: 'warning',
        label: `${unlinked} creator${unlinked === 1 ? '' : 's'} not linked to Infloww`,
        detail: 'Their revenue will not sync until they are connected',
        to: '/creators',
      });
    }

    return items.slice(0, 6);
  }, [tasks, vault, creators]);

  const openTasks = tasks.filter((t) => t.status !== 'complete' && t.status !== 'archived').length;

  const metrics = [
    { label: 'Gross revenue', value: money(totals.gross), change: delta(totals.gross, prevTotals.gross) },
    { label: 'Net revenue', value: money(totals.net), change: delta(totals.net, prevTotals.net) },
    { label: 'Agency profit', value: money(totals.agencyProfit), change: delta(totals.agencyProfit, prevTotals.agencyProfit) },
    { label: 'Active creators', value: String(creators.length), sub: `${leaderboard.length} earning` },
    { label: 'Open tasks', value: String(openTasks), sub: attention.length ? `${attention.length} need attention` : 'nothing overdue' },
    // Carried over when this became the only dashboard: the margin and the
    // payroll bill were the two figures the old admin page had that this did
    // not, and both come straight out of the totals already fetched.
    {
      label: 'Profit margin',
      value: totals.gross ? `${Math.round((totals.agencyProfit / totals.gross) * 100)}%` : '—',
      sub: 'agency share of gross',
    },
    ...(totals.totalPayrollPaid !== undefined
      ? [{ label: 'Paid to staff', value: money(totals.totalPayrollPaid), sub: 'commission and shifts' }]
      : []),
  ];

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <MainLayout currentPage="dashboard">
      <div className={styles.page}>
        <header className={styles.head}>
          <div className={styles.headText}>
            <h1 className={styles.greeting}>{greeting}, {user?.firstName || 'there'}</h1>
            <p className={styles.sub}>Here&rsquo;s what&rsquo;s happening across WTSN today.</p>
          </div>
          {/* Sits with the figures it controls. It used to live above the chart,
              which it never drove — the chart compares whole months. */}
          <div className={styles.ranges}>
            {RANGES.map((r) => (
              <button
                key={r.id}
                type="button"
                className={[styles.range, range === r.id ? styles.rangeOn : ''].filter(Boolean).join(' ')}
                onClick={() => setRange(r.id)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </header>

        {/* ── Headline figures ─────────────────────────────────────────────
            A row of numbers separated by rules, not five bordered boxes. The
            comparison is the point, so the delta sits with the figure. */}
        <section className={styles.metrics} data-responsive="own">
          {metrics.map((m) => (
            <div key={m.label} className={styles.metric}>
              <div className={styles.metricLabel}>{m.label}</div>
              <div className={styles.metricValue}>
                {loading ? <Skeleton width="90px" height="30px" /> : m.value}
              </div>
              {!loading && (m.change != null ? (
                <div className={[styles.metricChange, m.change >= 0 ? styles.up : styles.down].join(' ')}>
                  {m.change >= 0 ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
                  {Math.abs(m.change).toFixed(1)}%
                  <span className={styles.metricVs}>vs previous {days}d</span>
                </div>
              ) : (
                <div className={styles.metricSub}>{m.sub || '—'}</div>
              ))}
            </div>
          ))}
        </section>

        {/* ── Revenue ─────────────────────────────────────────────────────── */}
        <section className={styles.section}>
          <div className={styles.sectionHead}>
            <div>
              <h2 className={styles.sectionTitle}>Net revenue</h2>
              <p className={styles.sectionSub}>{monthNames.now} against {monthNames.prev}, day by day</p>
            </div>
            <span className={styles.legend}>
              <span className={styles.legendNow}>{monthNames.now}</span>
              <span className={styles.legendPrev}>{monthNames.prev}</span>
            </span>
          </div>

          <div className={styles.chart}>
            {loading ? <Skeleton width="100%" height="260px" /> : series.length === 0 ? (
              <div className={styles.chartEmpty}>No revenue recorded in either month.</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={series} margin={{ top: 8, right: 4, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="wtsnNetMonth" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2DD4BF" stopOpacity={0.22} />
                      <stop offset="100%" stopColor="#2DD4BF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  {/* Horizontal rules only. A full grid is scaffolding the
                      reader has to look past to see the line. */}
                  <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.06)" />
                  <XAxis
                    dataKey="day" tickLine={false} axisLine={false}
                    tick={{ fill: '#667386', fontSize: 11 }} minTickGap={22}
                  />
                  <YAxis
                    tickLine={false} axisLine={false} width={64}
                    tick={{ fill: '#667386', fontSize: 11 }}
                    tickFormatter={(v) => `$${v >= 1000 ? `${Math.round(v / 1000)}k` : Math.round(v)}`}
                  />
                  <Tooltip
                    contentStyle={{
                      background: '#101923', border: '1px solid rgba(255,255,255,0.13)',
                      borderRadius: 10, fontSize: 12, color: '#F5F7FA',
                    }}
                    labelFormatter={(d) => `Day ${d}`}
                    formatter={(v, k) => [money2(v), k === 'thisMonth' ? monthNames.now : monthNames.prev]}
                  />
                  {/* Last month first, so this month draws on top of it. */}
                  <Area type="monotone" dataKey="lastMonth" stroke="#667386" strokeWidth={1.5}
                        fill="none" dot={false} strokeDasharray="3 3" connectNulls={false} />
                  <Area type="monotone" dataKey="thisMonth" stroke="#2DD4BF" strokeWidth={2}
                        fill="url(#wtsnNetMonth)" dot={false} activeDot={{ r: 3 }} connectNulls={false} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        {/* ── Two columns: who is earning, and what is blocked ─────────────── */}
        <div className={styles.split}>
          <section className={styles.section}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>Creator performance</h2>
              <Link to="/revenue" className={styles.more}>All revenue <ArrowRight size={13} /></Link>
            </div>

            {loading ? <Skeleton width="100%" height="200px" /> : leaderboard.length === 0 ? (
              <p className={styles.empty}>No creator revenue in this period.</p>
            ) : (
              <table className={styles.lb}>
                <thead>
                  <tr>
                    <th>Creator</th>
                    <th className={styles.num}>Gross</th>
                    <th className={styles.num}>Net</th>
                    <th className={styles.num}>Active</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.slice(0, 8).map((c) => (
                    <tr key={c.id}>
                      <td>
                        <Link to={`/revenue/creators/${c.id}`} className={styles.lbName}>{c.name}</Link>
                        {/* The bar is the ranking made visible — it reads
                            faster than comparing four-figure numbers. */}
                        <span className={styles.lbBar}>
                          <span className={styles.lbFill} style={{ width: `${Math.max(c.share, 2)}%` }} />
                        </span>
                      </td>
                      <td className={styles.num}>{money2(c.gross)}</td>
                      <td className={[styles.num, styles.dim].join(' ')}>{money2(c.net)}</td>
                      <td className={[styles.num, styles.dim].join(' ')}>{c.activeDays}d</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className={styles.section}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>Needs attention</h2>
            </div>

            {loading ? <Skeleton width="100%" height="200px" /> : attention.length === 0 ? (
              <div className={styles.clear}>
                <CheckCircle2 size={18} />
                <div>
                  <div className={styles.clearTitle}>Nothing needs you</div>
                  <div className={styles.clearSub}>No overdue tasks, no content waiting on review.</div>
                </div>
              </div>
            ) : (
              <ul className={styles.attn}>
                {attention.map((a) => (
                  <li key={a.key}>
                    <Link to={a.to} className={styles.attnRow}>
                      <span className={[styles.attnIcon, styles[a.tone]].join(' ')}>
                        {a.tone === 'danger' ? <TriangleAlert size={14} /> : <CircleDashed size={14} />}
                      </span>
                      <span className={styles.attnText}>
                        <span className={styles.attnLabel}>{a.label}</span>
                        <span className={styles.attnDetail}>{a.detail}</span>
                      </span>
                      <ArrowRight size={14} className={styles.attnGo} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            <div className={styles.quick}>
              <Link to="/creators" className={styles.quickRow}>
                <Users size={15} /> <span>Creators</span> <b>{creators.length}</b>
              </Link>
              <Link to="/content" className={styles.quickRow}>
                <FileText size={15} /> <span>Files in the vault</span> <b>{vault?.totalFiles ?? '—'}</b>
              </Link>
            </div>
          </section>
        </div>
      </div>
    </MainLayout>
  );
}

export default CommandCentre;
